import { useTranslation } from 'react-i18next';
import { StatusBadge, IconButton, Utils } from '@treezor/design-system';

import { useProfileContext } from '@/features/context/ProfileContext';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useI18nLocale } from '@/hooks/useI18nLocale';

import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { IWallet } from '@/features/wallets/store/models/wallet.model';
import { WalletStarred } from '@/features/wallets/components/WalletStarred';
import { WalletMenu } from '@/features/wallets/components/WalletMenu';
import { useUserWallet } from '@/features/wallets/hooks/useUserWallet';

type TWalletItemProps = {
  wallet: IWallet;
  handleNavigate: (wallet: IWallet) => void;
};

export function WalletItem({ wallet, handleNavigate }: TWalletItemProps) {
  const { user } = useProfileContext();
  const { hasAccessToFeature } = useAccesControl();

  const { statusProps, walletType, isLoading, onChangeFavorite, isStarred } = useUserWallet({
    user,
    wallet,
  });
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const columnCss = 'flex flex-col items-start text-secondary typo-body-regular-paragraph';
  const warningCss = 'typo-body-semibold-paragraph text-icon-error';

  return (
    <div className="flex flex-col items-start w-full bg-primary border-2 px-6 py-5 border-primary rounded-200 gap-3">
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col">
          <span className="typo-body-semibold-paragraph text-secondary">{wallet.eventName || '-'}</span>
          <span className="typo-body-regular-caption text-tertiary">{`ID #${wallet.walletId}`}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="typo-body-regular-caption text-tertiary">{walletType}</span>
          <StatusBadge emphasis="low" size="s" className="w-fit" {...statusProps} />
        </div>
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-8">
          <div className={columnCss}>
            <span className="font-light">{t('pages.currentProfiles.wallets.wallet.iban')}</span>
            <span>{wallet.iban}</span>
          </div>
          <div className={columnCss}>
            <span className="font-light">{t('pages.currentProfiles.wallets.wallet.currentBalance')}</span>
            <span className={wallet.solde < 0 ? warningCss : ''}>
              {Utils.currencyFormater(locale, wallet.solde, 'EUR')}
            </span>
          </div>
          <div className={columnCss}>
            <span className="font-light">{t('pages.currentProfiles.wallets.wallet.authorizedBalance')}</span>
            <span className={wallet.authorizedBalance < 0 ? warningCss : ''}>
              {Utils.currencyFormater(locale, wallet.authorizedBalance, 'EUR')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <WalletStarred isLoading={isLoading} isStarred={isStarred} onClick={onChangeFavorite} />
          {hasAccessToFeature(featuresSlugs.PROFILES_BOARD) &&
            hasAccessToFeature(
              [
                featuresSlugs.ACCOUNT_DETAIL,
                featuresSlugs.STATEMENT,
                featuresSlugs.BALANCE_CERTIFICATE,
                featuresSlugs.DOMICILIATION_CERTIFICATE,
                featuresSlugs.CLOSURE_CERTIFICATE,
              ],
              'OR',
            ) && (
              <WalletMenu
                wallet={wallet}
                user={user}
                options={[
                  'accountDetails',
                  'statement',
                  'balanceCertificate',
                  'closureCertificate',
                  'domiciliationCertificate',
                ]}
              />
            )}
          <IconButton icon="chevron_right" size="s" variant="secondary" onClick={() => handleNavigate(wallet)} />
        </div>
      </div>
    </div>
  );
}
